import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { apiPaths } from '../../../service/apiPaths';
import ApiService from '../../../service/ApiService';
import {
    formatDateToISODateString, limit, updateComponent, ViewType
} from '../../../util/constant';
import { IClient } from './Client.props';

const useClient = () => {
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [clientData, setClientData] = useState<IClient[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false); // State to manage the visibility of the confirmation dialog
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [selectedClientForEmail, setSelectedClientForEmail] = useState<
    string | null
  >(null);

  const navigate = useNavigate();

  const handleDateFilter = async (
    fromDate: Date | null,
    toDate: Date | null
  ) => {
    setLoading(true);
    try {
      let endpoint = ``;
      if (fromDate && toDate) {
        const formattedFromDate = formatDateToISODateString(fromDate);
        const formattedToDate = formatDateToISODateString(toDate);
        endpoint = `${apiPaths.getAllClients}?fromDate=${formattedFromDate}&toDate=${formattedToDate}&page=${page}&limit=${limit}`;
        if (search) {
          endpoint = `${apiPaths.getAllClients}?fromDate=${formattedFromDate}&toDate=${formattedToDate}&search=${search}&page=${page}&limit=${limit}`;
        }
      } else {
        endpoint = `${apiPaths.getAllClients}?page=${page}&limit=${limit}`;
        if (search) {
          endpoint = `${apiPaths.getAllClients}?search=${search}&page=${page}&limit=${limit}`;
        }
      }
      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });
      setClientData(result?.data);
      setTotalPage(result.totalPages);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmStatusUp = async () => {
    setLoading(true);
    const status = clientData.find((client: any) => client._id === deletingId);
    const data = {
      status: status?.status.toString() === "Active" ? "InActive" : "Active",
    };
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateClient}${deletingId}`,
        data: data,
      });
      setClientData((prevData) =>
        prevData.map((client) =>
          client._id === deletingId
            ? {
                ...client,
                status:
                  client?.status?.toString() === "Active"
                    ? "InActive"
                    : "Active",
              }
            : client
        )
      );
    } catch (error) {
      console.error("Error deleting client:", error);
    } finally {
      setLoading(false);
    }
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleEdit = (id: string) => {
    setSelectedClientId(id);
  };

  const handleStatusUp = async (id: string) => {
    setDeletingId(id);
    setShowConfirmation(true);
  };
  const handleCancelStatusUp = () => {
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleAdd = () => {
    navigate("/addClient");
  };
  const updateScreen = (type: ViewType) => {
    updateComponent(type, () => {}, setSelectedClientId);
  };

  const handleRowSelection = (rowId: string) => {
    setSelectedRows((prev) => {
      if (prev.includes(rowId)) {
        return prev.filter((id) => id !== rowId);
      } else {
        return [...prev, rowId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedRows.length === clientData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(clientData.map((client) => client._id));
    }
  };
  const handleSendEmail = async (message: string) => {
    return console.log("message", message);
  };
  const handleSendComment = async (message: string) => {
    try {
      const clientIds = selectedClientForEmail
        ? [selectedClientForEmail]
        : selectedRows;
      if (clientIds.length === 0) {
        toast.error("Please select at least one client");
        return;
      }
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateClient}${clientIds}`,
        data: {
          comments: message,
        },
      });

      toast.success(`Email sent to ${clientIds.length} client(s)`);
      setSelectedRows([]);
      setSelectedClientForEmail(null);
      setIsEmailModalOpen(false);
    } catch (error) {
      toast.error(`Error sending email: ${error}`);
    }
  };

  const openEmailModal = (clientId?: string) => {
    if (clientId) {
      setSelectedClientForEmail(clientId);
    }
    setIsEmailModalOpen(true);
  };
  const handleSelectStatus = (e: any, id: any) => {
    const status = e.target.value;
    handleConfirmLeadStatusUp(id, status);
  };
  const handleConfirmLeadStatusUp = async (id: any, status: any) => {
    setLoading(true);
    const updateData = {
      leadStatus: status,
    };
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.leadStatus}${id._id}`,
        data: updateData,
      });
      toast.success("Lead status updated successfully");
      handleDateFilter(null, null);
    } catch (error: any) {
      console.error("Error updating Lead status:", error);
      toast.error(
        error?.response?.data?.message || "Error updating Lead status"
      );
    } finally {
      setLoading(false);
    }
  };
  return {
    selectedClientId,
    clientData,
    showConfirmation,
    handleAdd,
    handleEdit,
    handleStatusUp,
    handleCancelStatusUp,
    handleConfirmStatusUp,
    handleDateFilter,
    updateScreen,
    loading,
    setSearch,
    search,
    setPage,
    page,
    totalPage,
    openEmailModal,
    handleSendEmail,
    handleSendComment,
    selectedRows,
    handleRowSelection,
    handleSelectStatus,
    isEmailModalOpen,
    setIsEmailModalOpen,
    setSelectedClientForEmail,
    selectedClientForEmail,
    handleSelectAll,
  };
};

export default useClient;
